import React from 'react';
import axios from 'axios';
import { useInfiniteQuery, useQuery } from 'react-query';
import { format } from 'date-fns';
import Icon from '@mdi/react';
import { mdiEye, mdiFilter, mdiLoading, mdiMagnify } from '@mdi/js';

import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Grid, { GridItem } from '@/components/layout/Grid';
import { Accordion } from '@/components/layout/Accordion';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoData from '@/components/Formulario/CampoData';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import { useDebounce } from '@/lib/useDebounce';

// Só tipos: o vite não resolve import de runtime de @webip/shared. Os rótulos
// (ação, origem, entidade) vêm de GET /api/auditoria/opcoes.
import type {
  AuditoriaAcao,
  AuditoriaDTO,
  AuditoriaOpcoesDTO,
} from '@webip/shared/src/dto/auditoria';

import styles from './page.module.css';

type ResponseList = {
  ok: boolean;
  total: number;
  result: AuditoriaDTO[];
  cursor: number | null;
  retencaoDias: number;
};

const OPCOES_VAZIAS: AuditoriaOpcoesDTO = { entidades: [], acoes: [], origens: [] };

const classeAcao = (acao: AuditoriaAcao) => {
  switch (acao) {
    case 'criar':
      return styles.acaoCriar;
    case 'alterar':
      return styles.acaoAlterar;
    case 'excluir':
      return styles.acaoExcluir;
    case 'exportar':
    case 'importar':
    case 'sincronizar':
    case 'atualizar_sistema':
      return styles.acaoSaida;
    default:
      return styles.acaoAcesso;
  }
};

/** Valores vêm como jsonb: string, número, booleano, objeto ou nulo. */
const formatarValor = (valor: unknown): React.ReactNode => {
  if (valor === null || valor === undefined || valor === '')
    return <span className={styles.vazio}>(vazio)</span>;
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  if (typeof valor === 'object') return JSON.stringify(valor, null, 2);
  return String(valor);
};

const Page = () => {
  const [detalhe, setDetalhe] = React.useState<AuditoriaDTO | null>(null);

  const [dataIni, setDataIni] = React.useState('');
  const [dataFim, setDataFim] = React.useState('');
  const [usuarioId, setUsuarioId] = React.useState<string | null>(null);
  const [entidades, setEntidades] = React.useState<string[]>([]);
  const [acoes, setAcoes] = React.useState<string[]>([]);
  const [origens, setOrigens] = React.useState<string[]>([]);
  const [busca, setBusca] = React.useState('');
  const buscaDebounced = useDebounce(busca, 500);

  const queryOpcoes = useQuery({
    queryKey: ['auditoria-opcoes'],
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await axios.get<{ result: AuditoriaOpcoesDTO }>(
        '/api/auditoria/opcoes',
        { withCredentials: true },
      );
      return data.result;
    },
  });
  const opcoes = queryOpcoes.data ?? OPCOES_VAZIAS;

  const rotulos = React.useMemo(() => {
    const paraMapa = (lista: { id: string; label: string }[]) =>
      Object.fromEntries(lista.map(o => [o.id, o.label])) as Record<string, string>;
    return {
      acao: paraMapa(opcoes.acoes),
      origem: paraMapa(opcoes.origens),
      entidade: paraMapa(opcoes.entidades),
    };
  }, [opcoes]);

  const rotuloAcao = (acao: string) => rotulos.acao[acao] ?? acao;
  const rotuloOrigem = (origem: string) => rotulos.origem[origem] ?? origem;
  const rotuloEntidade = (entidade: string) => rotulos.entidade[entidade] ?? entidade;

  const queryUsuarios = useQuery({
    queryKey: ['auditoria-usuarios'],
    queryFn: async () => {
      const { data } = await axios.get<{ result: { id: number; nome: string }[] }>(
        '/api/usuario/box',
        { withCredentials: true },
      );
      return data.result ?? [];
    },
  });

  const opcoesUsuario = React.useMemo(
    () => (queryUsuarios.data ?? []).map(u => ({ id: String(u.id), label: u.nome })),
    [queryUsuarios.data],
  );

  const queryList = useInfiniteQuery({
    queryKey: ['auditoria', dataIni, dataFim, usuarioId, entidades, acoes, origens, buscaDebounced],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (dataIni) params.set('di', new Date(dataIni).toISOString());
      if (dataFim) params.set('df', new Date(dataFim).toISOString());
      if (usuarioId) params.set('u', usuarioId);
      if (entidades.length) params.set('e', entidades.join(','));
      if (acoes.length) params.set('a', acoes.join(','));
      if (origens.length) params.set('o', origens.join(','));
      if (buscaDebounced) params.set('txt', buscaDebounced);
      if (pageParam) params.set('c', String(pageParam));

      const { data } = await axios.get<ResponseList>(
        `/api/auditoria?${params.toString()}`,
        { withCredentials: true },
      );
      return data;
    },
    getNextPageParam: lastPage => lastPage.cursor ?? undefined,
  });

  const linhas = queryList.data?.pages?.flatMap(p => p.result) ?? [];
  const ultimaPagina = queryList.data?.pages?.[queryList.data.pages.length - 1];
  const total = ultimaPagina?.total ?? 0;
  const retencaoDias = ultimaPagina?.retencaoDias;

  const headers: DataTableHeader[] = [
    {
      label: 'Quando',
      width: '150px',
      render: (row: AuditoriaDTO) => format(new Date(row.data_criacao), 'dd/MM/yyyy HH:mm:ss'),
    },
    {
      label: 'Quem',
      width: '200px',
      render: (row: AuditoriaDTO) => (
        <div className={styles.registro}>
          <span className={styles.registroPrincipal}>{row.usuario_nome ?? 'Sistema'}</span>
          <span className={styles.registroId}>{row.papel ?? rotuloOrigem(row.origem)}</span>
        </div>
      ),
    },
    {
      label: 'O quê',
      width: '140px',
      render: (row: AuditoriaDTO) => (
        <span className={[styles.badge, classeAcao(row.acao)].join(' ')}>{rotuloAcao(row.acao)}</span>
      ),
    },
    {
      label: 'Entidade',
      width: '190px',
      render: (row: AuditoriaDTO) => rotuloEntidade(row.entidade),
    },
    {
      label: 'Registro',
      width: '25%',
      render: (row: AuditoriaDTO) => (
        <div className={styles.registro}>
          <span className={styles.registroPrincipal}>{row.descricao ?? '—'}</span>
          {row.entidade_id != null && <span className={styles.registroId}>#{row.entidade_id}</span>}
        </div>
      ),
    },
    {
      label: 'Origem',
      width: '140px',
      render: (row: AuditoriaDTO) => (
        <div className={styles.registro}>
          <span>{rotuloOrigem(row.origem)}</span>
          {row.ip && <span className={styles.registroId}>{row.ip}</span>}
        </div>
      ),
    },
    {
      label: 'Detalhes',
      width: '90px',
      stickRight: true,
      alignRight: true,
      render: (row: AuditoriaDTO) => (
        <Botao variant="none-primary" icon={mdiEye} title="Ver detalhes" onClick={() => setDetalhe(row)} />
      ),
    },
  ];

  const alteracoes = Object.entries(detalhe?.alteracoes ?? {});

  return (
    <LayoutUsuario>
      <Modal
        instanceId="modal-auditoria-detalhe"
        size="md"
        title="Detalhe do registro"
        show={!!detalhe}
        onClose={() => setDetalhe(null)}
        onCancel={() => setDetalhe(null)}
        cancelProps={{ label: 'Fechar' }}
        confirmProps={{ visible: false }}
      >
        {detalhe && (
          <VBox gap="12px">
            <div className={styles.meta}>
              {format(new Date(detalhe.data_criacao), 'dd/MM/yyyy HH:mm:ss')} ·{' '}
              {detalhe.usuario_nome ?? 'Sistema'} · {rotuloAcao(detalhe.acao)} em{' '}
              {rotuloEntidade(detalhe.entidade)}
              {detalhe.entidade_id != null ? ` #${detalhe.entidade_id}` : ''}
            </div>
            <div className={styles.meta}>
              {rotuloOrigem(detalhe.origem)}
              {detalhe.ip ? ` · IP ${detalhe.ip}` : ''}
              {detalhe.metodo && detalhe.rota ? ` · ${detalhe.metodo} ${detalhe.rota}` : ''}
            </div>

            {detalhe.detalhes && (
              <table className={styles.diff}>
                <thead>
                  <tr>
                    <th>Parâmetro</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(detalhe.detalhes).map(([chave, valor]) => (
                    <tr key={chave}>
                      <td className={styles.diffCampo}>{chave}</td>
                      <td className={styles.valor}>{formatarValor(valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {alteracoes.length === 0 && !detalhe.detalhes ? (
              <Descricao>Sem alteração de campos neste evento.</Descricao>
            ) : alteracoes.length === 0 ? null : (
              <table className={styles.diff}>
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Antes</th>
                    <th>Depois</th>
                  </tr>
                </thead>
                <tbody>
                  {alteracoes.map(([campo, valores]) => (
                    <tr key={campo}>
                      <td className={styles.diffCampo}>{campo}</td>
                      <td className={[styles.valor, styles.valorDe].join(' ')}>{formatarValor(valores.de)}</td>
                      <td className={[styles.valor, styles.valorPara].join(' ')}>{formatarValor(valores.para)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </VBox>
        )}
      </Modal>

      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        <PageHeader
          title="Auditoria"
          description={
            <>
              Quem fez o quê e quando: criações, alterações e exclusões nas configurações,
              logins, exportações e sincronismos disparados.
              {retencaoDias ? ` Os registros são mantidos por ${retencaoDias} dias.` : ''}
            </>
          }
        />

        <HBox>
          <CampoTexto
            prepend={<Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />}
            placeholder="Pesquisar por registro, usuário ou id"
            value={busca}
            onChange={setBusca}
          />
        </HBox>

        <Accordion
          title={
            <HBox gap="8px">
              <Icon path={mdiFilter} size={1} />
              Filtros
            </HBox>
          }
          startOpen={false}
        >
          <Grid>
            <GridItem sm={12} md={3}>
              <CampoData type="datetime-local" label="De" value={dataIni} onChange={setDataIni} />
            </GridItem>
            <GridItem sm={12} md={3}>
              <CampoData type="datetime-local" label="Até" value={dataFim} onChange={setDataFim} />
            </GridItem>
            <GridItem sm={12} md={6}>
              <CampoSeletor
                nullable
                label="Usuário"
                placeholder="(Qualquer)"
                value={usuarioId}
                onChange={setUsuarioId}
                options={opcoesUsuario}
              />
            </GridItem>
            <GridItem sm={12} md={4}>
              <CampoMultiSeletor
                label="Entidade"
                placeholder="(Todas)"
                value={entidades}
                onChange={setEntidades}
                options={opcoes.entidades}
              />
            </GridItem>
            <GridItem sm={12} md={4}>
              <CampoMultiSeletor
                noSearch
                label="Ação"
                placeholder="(Todas)"
                value={acoes}
                onChange={setAcoes}
                options={opcoes.acoes}
              />
            </GridItem>
            <GridItem sm={12} md={4}>
              <CampoMultiSeletor
                noSearch
                label="Origem"
                placeholder="(Todas)"
                value={origens}
                onChange={setOrigens}
                options={opcoes.origens}
              />
            </GridItem>
          </Grid>
        </Accordion>

        {queryList.isFetching ? (
          <div>
            Aguarde... <Icon path={mdiLoading} size="14px" color="var(--tc-color-gray-700)" spin />
          </div>
        ) : (
          <div>
            Exibindo {linhas.length} de {total} registros.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={linhas}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default Page;
